/**
 * 工厂端 - 订单与物流（只读查看，支持详情跳转）
 *
 * 物流信息从电商平台同步，工厂不需要手动回传。
 * 支持 ?detail=订单号 直接查看订单详情（发票页跳转用）。
 */

import { useSearchParams } from 'react-router-dom'
import { Card, Table, Badge, useApi, money, time, Button, Loading } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Order {
  order_id: string
  amount_total: string
  status: string
  logistics_no: string | null
  synced_at: string
  has_settlement?: boolean
}

export function FactoryShipping() {
  const list = useApi<Order[]>(async () => api.get('/orders'), [])
  const [searchParams, setSearchParams] = useSearchParams()
  const detailId = searchParams.get('detail')

  if (detailId) {
    return <FactoryOrderDetail orderId={detailId} onBack={() => setSearchParams({}, { replace: true })} />
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>订单与物流</h2>

      <div style={{ background: '#dbeafe', border: '1px solid #93c5fd', color: '#1e40af', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
        ℹ️ 物流信息从电商平台同步，工厂为寄件人（证明商品所有权未转移至商家）。无需手动回传。
      </div>

      <Card title={`代销订单（${list.data?.length ?? 0}）`}>
        <Table<Order>
          rows={list.data ?? []}
          empty={list.loading ? '加载中...' : '暂无订单'}
          columns={[
            { key: 'order_id', title: '订单号' },
            { key: 'amount_total', title: '金额', render: (r) => money(r.amount_total) },
            { key: 'logistics_no', title: '运单号', render: (r) => r.logistics_no ?? '-' },
            { key: 'synced_at', title: '同步时间', render: (r) => time(r.synced_at) },
            { key: 'has_settlement', title: '分账', render: (r) => (r.has_settlement ? '✓ 已分账' : '-') },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            {
              key: 'action', title: '操作',
              render: (r) => <Button size="sm" onClick={() => setSearchParams({ detail: r.order_id })}>详情</Button>,
            },
          ]}
        />
      </Card>

      <HelpBubble pageKey="factory-shipping" title="订单与物流">
        <p>工厂作为<b>委托方/货主</b>，商品所有权归工厂。物流信息从电商平台同步（工厂为寄件人），无需手动回传。</p>
        <p>点「详情」查看订单的物流、分账、商品票。</p>
      </HelpBubble>
    </div>
  )
}

function FactoryOrderDetail({ orderId, onBack }: { orderId: string; onBack: () => void }) {
  const detail = useApi<any>(async () => api.get(`/orders/${orderId}`), [orderId])

  if (detail.loading) return <Loading />
  if (detail.error || !detail.data) return <Loading error={detail.error || '无数据'} />

  const { order, logistics, invoices } = detail.data
  const goodsInvoices = (invoices ?? []).filter((i: any) => i.invoice_category === 'GOODS')

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button size="sm" variant="default" onClick={onBack}>← 返回</Button>
        <h2 style={{ margin: 0 }}>订单 {orderId} <Badge status={order.status} /></h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="订单信息">
          <div style={{ fontSize: 13 }}>
            <Field label="订单金额" value={money(order.amount_total)} />
            <Field label="电商原单号" value={order.platform_order_id} />
            <Field label="物流单号" value={order.logistics_no ?? '-'} />
            <Field label="同步时间" value={time(order.synced_at)} />
          </div>
        </Card>

        <Card title="物流详情">
          {logistics?.length > 0 ? (
            <div style={{ fontSize: 13 }}>
              {logistics.map((l: any) => (
                <div key={l.logistics_id}>
                  <Field label="物流公司" value={l.carrier} />
                  <Field label="运单号" value={l.tracking_no} />
                  <Field label="寄件人" value={`${l.shipper_id}（本工厂）`} />
                  <Field label="状态" value={l.status} />
                </div>
              ))}
            </div>
          ) : <div style={{ color: '#888' }}>无物流</div>}
        </Card>
      </div>

      <Card title="商品票（工厂开具）">
        {goodsInvoices.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {goodsInvoices.map((inv: any) => (
              <div key={inv.invoice_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                {inv.pdf_url && inv.status === 'ISSUED' ? (
                  <img src={inv.pdf_url} alt={inv.invoice_no} style={{ width: '100%', display: 'block' }} />
                ) : (
                  <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', color: '#888' }}>{inv.status}</div>
                )}
                <div style={{ padding: 10, fontSize: 12 }}>
                  <div style={{ fontWeight: 600 }}>商品票（13%）<Badge status={inv.status} /></div>
                  <div style={{ color: '#666' }}>发票号：{inv.invoice_no || '-'}</div>
                  <div style={{ color: '#666' }}>金额：{money(inv.amount)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : <div style={{ color: '#888', fontSize: 13 }}>未开票</div>}
      </Card>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  )
}
